import {
  ApplicationConfig,
  APP_INITIALIZER,
  importProvidersFrom,
  provideZoneChangeDetection,
  inject,
  provideAppInitializer,
  ErrorHandler,
} from '@angular/core';
import { provideRouter, Router, RouterModule, withComponentInputBinding } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { provideAnimations } from '@angular/platform-browser/animations';
import { providePrimeNG } from 'primeng/config';
import Noir from '@/themes/doktar-preset';
import { routes } from './app.routes';
import { provideHttpClient, withInterceptors, HttpClient, HttpBackend } from '@angular/common/http';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { environment } from '@/environments/environment';
import { XAppInterceptor } from './core/interceptors/x-app.interceptor';
import { PLAIN_HTTP_CLIENT } from './core/tokens/http-tokens';
import { AuthService } from './auth/shared/services/auth.service';
import { NavigationModule } from './core/services/navigation.module';
import { NavigationInterceptorService } from './core/interceptors/navigation-interceptor.service';
import { LanguageInitializerService } from './core/services/language-initializer.service';
import {
  ConfigurationProxy,
  LookupProxy,
  PlantationProxy,
  UserProxy,
  NotificationProxy,
  API_BASE_URL,
  EnterpriseProxy,
  DeviceLineProxy,
} from './proxy/proxies';
import { AppInitializerService } from './core/services/app-initializer.service';
import { UserService } from './core/services/user.service';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { provideServiceWorker } from '@angular/service-worker';
import { ApiToastInterceptor } from './core/interceptors/api-toast.interceptor';
import { ForbiddenInterceptor } from './core/interceptors/forbidden.interceptor';
import { AppConfigService } from './core/services/app-config.service';
import { ConfigAwareTranslateLoader } from './core/services/config-aware-translate-loader.service';
import { AnalyticsService } from './core/services/analytics.service';
import { EchartsInitService } from './core/services/echarts-init.service';
import { PermissionService } from './core/services/permission.service';
import { AppLoadingService } from './app-loading.service';
import { initSentryFromDsn } from './core/sentry/sentry-init';
import { getClient } from '@sentry/angular';

const httpLoaderFactory = () => {
  return inject(ConfigAwareTranslateLoader);
};

// Factory function for language initializer
export function initializeLanguageFactory(langInitService: LanguageInitializerService) {
  return () => langInitService.initializeLanguage();
}

/** Redirect csi.doktar hostname to sustainability.doktar before app loads 
 * TODO: Remove this once the app is deployed to sustainability.doktar
*/
function redirectCsiToSustainability(): Promise<void> {
  if (typeof window !== 'undefined' && window.location.hostname.includes('csi.doktar')) {
    const newHost = window.location.hostname.replace('csi.doktar', 'sustainability.doktar');
    const newUrl = `${window.location.protocol}//${newHost}/sustainability-dashboard${window.location.search}`;
    window.location.assign(newUrl);
    return new Promise(() => {}); // Never resolve so app does not continue loading
  }
  return Promise.resolve();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(redirectCsiToSustainability),
    // Default HttpClient with interceptors
    provideHttpClient(withInterceptors([XAppInterceptor, ForbiddenInterceptor, ApiToastInterceptor])),
    // Plain HttpClient without interceptors
    {
      provide: PLAIN_HTTP_CLIENT,
      useFactory: (backend: HttpBackend) => {
        return new HttpClient(backend);
      },
      deps: [HttpBackend],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        return () => {
          // Sentry tracing is initialized in main.ts
        };
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
    // Add LookupProxy as app-level provider
    LookupProxy, //TODO: This should be not here, it should be in the module where it is used initializers
    UserProxy, //TODO: This should be not here, it should be in the module where it is used initializers
    PlantationProxy,
    EnterpriseProxy,
    ConfigurationProxy,
    NotificationProxy,
    DeviceLineProxy,
    // Provide API base URL from environment
    {
      provide: API_BASE_URL,
      useValue: environment.apiBaseUrl,
    },
    provideAppInitializer(async () => {
      const appConfigService = inject(AppConfigService);
      const langInitService = inject(LanguageInitializerService);
      const appInitService = inject(AppInitializerService);
      const userService = inject(UserService);
      const analyticsService = inject(AnalyticsService);
      const echartsInitService = inject(EchartsInitService);
      const permissionService = inject(PermissionService);
      const appLoadingService = inject(AppLoadingService);

      try {
        appLoadingService.showLoading();

        await appConfigService.initializeApp();

        // DSN lives in authenticated config (`App.Web.Sentry.DSN`); no-op here until after login unless cached auth config exists
        initSentryFromDsn(appConfigService.sentryDsn);

        // First initialize language
        await langInitService.initializeLanguage();

        // Then initialize app
        await appInitService.initializeApp();

        // Initialize user service (this will also initialize permissions)
        const userInitResult = await userService.initializeUserService();

        // Note: SignalR notifications are now bootstrapped in FeaturesComponent (authenticated shell)
        // instead of polling here. This ensures proper auth state and config are ready.

        // Initialize analytics (this will start tracking automatically)
        // No need to await as it's not critical for app startup
        analyticsService.getStatus();

        // Initialize ECharts components
        echartsInitService.getEchartsInstance();
        return userInitResult;
      } catch (error) {
        console.error('Failed to initialize application:', error);
        if (getClient()) {
          Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
            tags: { component: 'AppInitializer' },
            extra: { initializationStep: 'app-bootstrap' },
            level: 'error',
          });
        }
        return false;
      } finally {
        userService.currentUser$.subscribe((user) => {
          if (user?.id) {
            permissionService.permission$.subscribe(res => {
              if (res) {
                setTimeout(() => {
                  appLoadingService.hideLoading();
                }, 100);
              }
            });
          } else {
            appLoadingService.hideLoading();
          }
        });
      }
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    AuthService,
    // Navigation services
    NavigationInterceptorService,
    MessageService,
    DialogService,
    provideAnimations(),
    providePrimeNG({
      theme: Noir,
      overlayAppendTo: 'body',
    }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [],
      },
    }),
    importProvidersFrom(RouterModule.forRoot([...routes]), NavigationModule.forRoot()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.enableServiceWorker,
      registrationStrategy: 'registerImmediately',
    }),
  ],
};
